import { action, computed, observable, makeObservable } from "mobx";
import { MrbBaseViewStore } from "mrb/core";
import { MrbSelectStore } from "mrb/components/select/stores";
import { map, sumBy, some, uniqBy, isNil } from "lodash";

// Local producer row mirroring the naki-nigun editor row. The staged import editor holds producer
// assignments in memory (they are persisted later through the batch metadata save), so this row
// never talks to the API directly.
class ProducerRow {
    @observable producerId = null;
    @observable revenuePercentage = 0;
    producerSelectStore = null;

    constructor(findProducers, existingProducer = null) {
        makeObservable(this);

        this.producerSelectStore = new MrbSelectStore({
            isSearchable: true,
            virtualized: false,
            initFetch: false,
            textKey: "name",
            valueKey: "id",
            isClearable: true,
            actions: {
                find: (searchTerm) => findProducers(searchTerm),
                onChange: action((selectedOption) => {
                    this.producerId = selectedOption ? selectedOption.value : null;
                }),
            },
        });

        if (existingProducer) {
            this.producerId = existingProducer.producerId;
            this.revenuePercentage = existingProducer.revenuePercentage;
            if (existingProducer.producer) {
                this.producerSelectStore.setSelectedItem(existingProducer.producer);
            }
        }
    }
}

class NakiNigunImportProducerEditViewStore extends MrbBaseViewStore {
    @observable producerRows = [];
    // The presentational template reads `trackTitle` for its header; it is really the target label
    // (album title, track title, "all albums", or "selected items").
    trackTitle = "";
    // Full, context-correct heading for the editor (e.g. "Album producers — bamidbar"). When set it
    // overrides the shared template's default "Edit Track Producers - <trackTitle>".
    heading = "";
    // When true, the editor shows a "skip albums that already have producers" checkbox (used by the
    // batch-wide assign-to-all-albums flow). `keepExisting` carries that choice back through onApply.
    allowKeepExisting = false;
    @observable keepExisting = false;

    @computed get totalRevenue() {
        return sumBy(this.producerRows, (row) => row.revenuePercentage);
    }

    @computed get isSaving() {
        return false;
    }

    @computed get isValid() {
        if (this.producerRows.length === 0) {
            return true;
        }
        if (some(this.producerRows, (row) => isNil(row.producerId))) {
            return false;
        }
        if (some(this.producerRows, (row) => row.revenuePercentage < 0.01)) {
            return false;
        }
        const uniqueProducers = uniqBy(this.producerRows, (row) => row.producerId);
        if (uniqueProducers.length !== this.producerRows.length) {
            return false;
        }
        return Math.round(this.totalRevenue * 100) / 100 === 100;
    }

    constructor(rootStore, { findProducers, producers, title, heading, allowKeepExisting, onApply }) {
        super(rootStore);
        makeObservable(this);
        this.findProducers = findProducers;
        this.trackTitle = title || "";
        this.heading = heading || "";
        this.allowKeepExisting = allowKeepExisting || false;
        this.onApply = onApply;

        if (producers && producers.length > 0) {
            this.producerRows = producers.map(
                (p) =>
                    new ProducerRow(findProducers, {
                        producerId: p.producerId,
                        revenuePercentage: p.revenuePercentage,
                        producer: { id: p.producerId, name: p.name },
                    })
            );
        }
    }

    @action.bound
    addRow() {
        this.producerRows.push(new ProducerRow(this.findProducers));
    }

    @action.bound
    removeRow(index) {
        this.producerRows.splice(index, 1);
    }

    @action.bound
    setRowRevenue(index, value) {
        const parsed = parseFloat(value);
        this.producerRows[index].revenuePercentage = isNaN(parsed)
            ? 0
            : Math.round(Math.max(0, Math.min(100, parsed)) * 100) / 100;
    }

    @action.bound
    setKeepExisting(value) {
        this.keepExisting = value;
    }

    @action.bound
    save() {
        if (!this.isValid) {
            return;
        }
        const producers = map(this.producerRows, (row) => ({
            producerId: row.producerId,
            revenuePercentage: row.revenuePercentage,
            name: row.producerSelectStore.selectedItem ? row.producerSelectStore.selectedItem.name : null,
        }));
        if (this.onApply) {
            this.onApply(producers, this.keepExisting);
        }
    }
}

export default NakiNigunImportProducerEditViewStore;
